import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Lock, Unlock, X, KeyRound, ShieldCheck } from "lucide-react";
import { useAdmin } from "../contexts/AdminContext";

/**
 * Admin lock/unlock gate.
 * - Renders a key icon (lock/unlock) suitable to drop into the header.
 * - When clicked, opens a code-entry popup. Correct code = 0913 unlocks edit mode.
 * - When already unlocked, clicking the icon offers to lock again.
 */
export default function AdminGate({ isNight = true, silent = false }) {
    const accent = isNight ? "#D4AF37" : "#B89B5E";
    const { isAdmin, unlock, lock, gateOpen, setGateOpen } = useAdmin();

    const [code, setCode] = useState("");
    const [error, setError] = useState(null);
    const [shake, setShake] = useState(false);
    const codeRef = useRef(null);

    useEffect(() => {
        if (gateOpen) {
            setCode("");
            setError(null);
            setTimeout(() => codeRef.current?.focus(), 100);
        }
    }, [gateOpen]);

    const handleSubmit = (e) => {
        e?.preventDefault?.();
        if (unlock(code)) {
            setGateOpen(false);
        } else {
            setError("Wrong code. Try again.");
            setShake(true);
            setCode("");
            setTimeout(() => setShake(false), 500);
            setTimeout(() => codeRef.current?.focus(), 50);
        }
    };

    const onIconClick = () => {
        if (isAdmin) {
            // toggle off
            lock();
        } else {
            setGateOpen(true);
        }
    };

    return (
        <>
            {!silent && (
                <button
                    type="button"
                    onClick={onIconClick}
                    data-testid="admin-lock-toggle"
                    className="relative w-11 h-11 rounded-full grid place-items-center transition-all"
                    style={{
                        background: isAdmin
                            ? accent
                            : (isNight ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"),
                        color: isAdmin ? "#0a0a0a" : accent,
                        border: `1px solid ${accent}55`,
                        boxShadow: isAdmin ? `0 0 18px ${accent}55` : "none",
                    }}
                    title={isAdmin ? "Admin mode ON — click to lock" : "Admin mode OFF — click to unlock"}
                >
                    {isAdmin ? <Unlock size={16} /> : <Lock size={16} />}
                </button>
            )}

            {gateOpen && createPortal((
                <div
                    data-testid="admin-gate-modal"
                    className="fixed inset-0 z-[200] flex items-center justify-center px-4"
                    style={{ background: "rgba(0,0,0,0.84)", backdropFilter: "blur(10px)" }}
                    onClick={(e) => { if (e.target === e.currentTarget) setGateOpen(false); }}
                >
                    <form
                        onSubmit={handleSubmit}
                        className={`glass rounded-2xl p-7 w-full max-w-sm text-center ${shake ? "animate-pulse" : ""}`}
                        style={{
                            border: `1px solid ${accent}55`,
                            boxShadow: `0 30px 80px -20px ${accent}66`,
                            transform: shake ? "translateX(-2px)" : "none",
                            transition: "transform 0.08s ease-in-out",
                        }}
                    >
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase" style={{ color: accent }}>
                                <ShieldCheck size={12} /> Admin Access
                            </div>
                            <button
                                type="button"
                                onClick={() => setGateOpen(false)}
                                data-testid="admin-gate-close"
                                style={{ color: "rgba(255,255,255,0.7)" }}
                            >
                                <X size={16} />
                            </button>
                        </div>
                        <div
                            className="mx-auto my-5 w-16 h-16 rounded-full grid place-items-center"
                            style={{
                                background: `${accent}18`,
                                border: `1px solid ${accent}80`,
                                color: accent,
                            }}
                        >
                            <KeyRound size={26} />
                        </div>
                        <div
                            className="font-serif-display text-2xl uppercase tracking-luxe mb-1"
                            style={{ color: "#fff" }}
                        >
                            Enter Code
                        </div>
                        <p
                            className="text-xs mb-5"
                            style={{ color: "rgba(255,255,255,0.6)" }}
                        >
                            Only admins can edit Caprio Chateau.
                        </p>
                        <input
                            ref={codeRef}
                            type="password"
                            inputMode="numeric"
                            pattern="\d*"
                            maxLength={6}
                            value={code}
                            onChange={(e) => { setCode(e.target.value); setError(null); }}
                            data-testid="admin-gate-input"
                            placeholder="••••"
                            className="w-full text-center text-2xl tracking-[0.5em] py-3 rounded-xl outline-none mb-3"
                            style={{
                                background: "rgba(255,255,255,0.05)",
                                color: "#fff",
                                border: `1px solid ${accent}60`,
                                fontFamily: "'Courier New', monospace",
                            }}
                        />
                        {error && (
                            <div
                                data-testid="admin-gate-error"
                                className="text-xs mb-3"
                                style={{ color: "#ff7a7a" }}
                            >
                                {error}
                            </div>
                        )}
                        <button
                            type="submit"
                            data-testid="admin-gate-submit"
                            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[11px] tracking-[0.35em] uppercase"
                            style={{ background: accent, color: "#0a0a0a" }}
                        >
                            Unlock
                        </button>
                    </form>
                </div>
            ), document.body)}
        </>
    );
}
